import { useState } from 'react';
import Avatar from './Avatar';
import './charts.css';

/* Small charts for Buffy's cards and the homework report (2026-10-13). Plain
   SVG so they stay crisp and light. One rule for color, checked with the
   dataviz validator: quiet gray-green for everything, and one accent (rust)
   only for what needs the teacher — the low scores, the weakest topic. Gray vs
   rust stays apart under color blindness (ΔE 14.6); labels and the student
   list carry the exact values, so color is never the only channel. */

export const INK = '#2f3a27';
export const QUIET = '#9aa98a';
export const ACCENT = '#c2502c';
export const TRACK = '#eef1ea';
const DONE = '#3b6d11';
const WORKING = '#b9d39f';

// Completion as a ring: done (dark), still working (light), the rest track.
export function Ring({ done = 0, working = 0, total = 0, size = 56, stroke = 7, label }) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const part = n => (total ? Math.max(0, Math.min(1, n / total)) * c : 0);
  const d = part(done); const w = part(working);
  // A 2px gap between the two arcs (surface gap), only when both show.
  const gap = d && w ? 2 : 0;
  return <svg className="ch-ring" width={size} height={size} viewBox={`0 0 ${size} ${size}`} role="img" aria-label={label || `${done} of ${total} finished`}>
    <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={TRACK} strokeWidth={stroke} />
    {w > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={WORKING} strokeWidth={stroke} strokeLinecap="butt"
      strokeDasharray={`${Math.max(0, w - gap)} ${c}`} strokeDashoffset={-(d + gap)} transform={`rotate(-90 ${size / 2} ${size / 2})`} />}
    {d > 0 && <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={DONE} strokeWidth={stroke} strokeLinecap={d >= c - 0.5 ? 'butt' : 'round'}
      strokeDasharray={`${d} ${c}`} transform={`rotate(-90 ${size / 2} ${size / 2})`} />}
  </svg>;
}

// Everyone's score as a dot on 0–100: the spread and who is low at a glance.
// Dots that would overlap stack upward and the chart grows to fit the tallest
// stack (short sets put whole classes on a few scores). Low scores (under
// `low`) wear the accent; the lowest `named` are labeled, by first name unless
// first names repeat.
export function DotStrip({ points, low = 50, named = 2, average = null }) {
  const [hover, setHover] = useState(null);
  const shown = points.filter(p => p.value != null);
  const dense = shown.length > 20;
  const dot = dense ? 10 : 12;
  const pitch = dot + 1;
  const step = dense ? 3 : 4; // % per column; dots in one column stack
  const cols = new Map();
  const placed = [...shown].sort((a, b) => a.value - b.value).map(p => {
    const col = Math.round(p.value / step);
    const row = cols.get(col) || 0; cols.set(col, row + 1);
    return { ...p, row };
  });
  const tallest = Math.max(1, ...cols.values());
  const base = 22;
  const height = base + tallest * pitch + 22;
  const firsts = shown.map(p => String(p.label).split(' ')[0]);
  const short = label => { const f = String(label).split(' ')[0]; return firsts.filter(x => x === f).length > 1 ? label : f; };
  const lowest = placed.filter(p => p.value < low).slice(0, dense ? 1 : named).map(p => p.id);
  return <div className="ch-strip" style={{ height }} role="img" aria-label={`Scores: ${shown.map(p => `${p.label} ${Math.round(p.value)}%`).join(', ')}`}>
    <div className="ch-strip-axis" aria-hidden="true"><span style={{ left: '0%' }}>0</span><span style={{ left: '50%' }}>50</span><span style={{ left: '100%' }}>100%</span></div>
    {average != null && <div className="ch-strip-avg" style={{ left: `${average}%` }} aria-hidden="true"><span>avg {Math.round(average)}%</span></div>}
    {placed.map(p => <button type="button" key={p.id} className={`ch-dot${p.value < low ? ' is-low' : ''}`} aria-label={`${p.label}: ${Math.round(p.value)}%`}
      style={{ left: `${p.value}%`, bottom: base + p.row * pitch, width: dot, height: dot, marginLeft: -dot / 2 }}
      onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)} onFocus={() => setHover(p)} onBlur={() => setHover(null)}>
      {lowest.includes(p.id) && hover?.id !== p.id && <span className="ch-dot-label" style={{ bottom: (cols.get(Math.round(p.value / step)) - p.row) * pitch + 2 }}>{short(p.label)}</span>}
    </button>)}
    {hover && <div className="ch-tip" style={{ left: `${hover.value}%`, bottom: base + hover.row * pitch + dot + 6 }}>{hover.label} · {Math.round(hover.value)}%</div>}
  </div>;
}

// The score spread as a thumbnail (the brief's homework tile): dots on a
// hairline, stacked where they meet, low scores in the accent. Decorative; the
// tile's label carries the numbers.
export function MiniDots({ values, low = 50, width = 132, height = 40 }) {
  const shown = values.filter(v => v != null);
  if (!shown.length) return null;
  const r = 3.5; const pitch = 8; const base = height - 6;
  const cols = new Map();
  const dots = [...shown].sort((a, b) => a - b).map(v => {
    const col = Math.round(v / 5); const row = cols.get(col) || 0; cols.set(col, row + 1);
    return { v, x: r + (v / 100) * (width - 2 * r), y: Math.max(r, base - r - row * pitch) };
  });
  return <svg className="ch-minidots" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
    <line x1="0" x2={width} y1={base + 0.5} y2={base + 0.5} stroke="#E5E5E5" />
    {dots.map((d, i) => <circle key={i} cx={d.x} cy={d.y} r={r} fill={d.v < low ? ACCENT : QUIET} />)}
  </svg>;
}

// A labeled horizontal bar. `emphasis` paints it in the accent.
export function Bar({ label, value, emphasis = false, after = null }) {
  return <div className="ch-bar">
    <span className="ch-bar-label">{label}</span>
    <span className="ch-bar-track" aria-hidden="true"><i style={{ width: `${Math.max(2, Math.min(100, value || 0))}%`, background: emphasis ? ACCENT : QUIET }} /></span>
    <span className="ch-bar-value">{value == null ? '–' : `${Math.round(value)}%`}</span>
    {after}
  </div>;
}

// Faces instead of a list of names; the rest as +N.
export function Faces({ names, max = 4, size = 24, as = 'div' }) {
  if (!names?.length) return null;
  return <span className="ch-faces" style={{ '--ch-overlap': `${Math.round(size * 0.15)}px` }} title={names.join(', ')} aria-label={names.join(', ')}>
    {names.slice(0, max).map(n => <Avatar key={n} name={n} size={size} as={as} />)}
    {names.length > max && <span className="ch-faces-more" style={{ height: size, minWidth: size }}>+{names.length - max}</span>}
  </span>;
}

// A big number with a short label under it.
export function Stat({ value, label, sub = null }) {
  return <div className="ch-stat"><span className="ch-stat-value">{value}</span><span className="ch-stat-label">{label}</span>{sub}</div>;
}
